import React from 'react';
import { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Tabs,
  Tab,
  Card,
  CardContent,
  Chip
} from '@mui/material';
import axios from 'axios';

const GenerationStats = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tabValue, setTabValue] = useState(0);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/admin/generations`);
        setData(response.data);
      } catch (err) {
        setError(err.response?.data?.message || 'Error fetching generation stats');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  if (loading) return <CircularProgress />;
  if (error) return <Typography color="error">{error}</Typography>;

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Generation Statistics
      </Typography>

      <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)} sx={{ mb: 3 }}>
        <Tab label="User Statistics" />
        <Tab label="Recent Generations" />
        <Tab label="Daily Stats" />
      </Tabs>

      {tabValue === 0 && (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>User</TableCell>
                <TableCell align="right">Total Generations</TableCell>
                <TableCell align="right">Today's Generations</TableCell>
                <TableCell align="right">Posts</TableCell>
                <TableCell align="right">Images</TableCell>
                <TableCell>Last Generation</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.userGenerations.map((user) => (
                <TableRow key={user.user_id}>
                  <TableCell>
                    <Typography variant="body2">{user.username}</Typography>
                    <Typography variant="caption" color="textSecondary">
                      {user.email}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">{user.total_generations}</TableCell>
                  <TableCell align="right">
                    <Chip 
                      label={user.generations_today}
                      color={user.generations_today > 0 ? "success" : "default"}
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="right">{user.post_generations}</TableCell>
                  <TableCell align="right">{user.image_generations}</TableCell>
                  <TableCell>
                    {user.last_generation ? 
                      new Date(user.last_generation).toLocaleString() : 
                      'Never'
                    }
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {tabValue === 1 && (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>User</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Content</TableCell>
                <TableCell>Generated At</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.recentGenerations.map((gen) => (
                <TableRow key={gen.id}>
                  <TableCell>
                    <Typography variant="body2">{gen.username}</Typography>
                    <Typography variant="caption" color="textSecondary">
                      {gen.email}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={gen.type}
                      color={gen.type === 'image' ? "secondary" : "primary"}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>{gen.content.substring(0, 50)}...</TableCell>
                  <TableCell>{new Date(gen.created_at).toLocaleString()}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {tabValue === 2 && (
        <Box>
          <Typography variant="h6" gutterBottom>
            Last 7 Days Generation Activity
          </Typography>
          {data.dailyStats.map((day) => (
            <Card key={day.date} sx={{ mb: 2 }}>
              <CardContent>
                <Typography variant="h6">
                  {new Date(day.date).toLocaleDateString()}
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
                  <Chip label={`Total: ${day.total}`} />
                  <Chip label={`Posts: ${day.posts}`} color="primary" />
                  <Chip label={`Images: ${day.images}`} color="secondary" />
                </Box>
              </CardContent>
            </Card>
          ))}
        </Box>
      )}
    </Box>
  );
};

export default GenerationStats; 